import { MasterOpSchema, isOfflineRegisterTable, type MasterOp } from '@trackeat/shared';
import { normalizeLegacyPrinterMaster } from '../sync/legacyPrinterMaster.js';
import { toUserMessage } from '../errors.js';
import { CapacitorSQLite, SQLiteConnection, type SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { beginNativePerfOperation, finishNativePerfSpan, startNativePerfOperation } from '../debug/nativePerfTrace.js';
import {
  RECOVERABLE_SYNC_CONTRACT_REASONS,
  isRecoverableSyncContractEvent,
  confirmedEquivalentCleaning,
  type ConfirmedCleaningRecoveryEvent,
} from '../sync/contractRejectedEventRecovery.js';

const DB_NAME = 'niveo-local';
const DB_VERSION = 1;
const DEVICE_ID_KEY = 'niveo.device_id';
const PBKDF2_ITER = 120_000;
const PBKDF2_SALT = 'niveo/sqlcipher/v1';

const sqlite = new SQLiteConnection(CapacitorSQLite);

let connection: SQLiteDBConnection | null = null;
let openingPromise: Promise<SQLiteDBConnection> | null = null;
let resettingPromise: Promise<void> | null = null;

// Serializzazione globale delle operazioni SQL sulla DB locale.
// Why: jeep-sqlite (sql.js, web) non ha mutex interna fra `db.run/query/execute`
// concorrenti. Con due chiamate in volo simultanee la seconda emette `BEGIN`
// mentre la prima è ancora dentro la sua transaction di default, e il plugin
// rigetta con "RunSQL: BeginTransaction: cannot start a transaction within a
// transaction". La fonte tipica sono i logger fire-and-forget
// (`void appendDebugEvent(...)` in debug/debugLog.ts, `void connection.run(...)`
// in logDbg qui sotto) che partono mentre il chiamante awaita la propria run.
// Wrappiamo la connection in un Proxy che instrada ogni op SQL in una
// promise-chain seriale: anche i fire-and-forget restano ordinati.
let opQueue: Promise<unknown> = Promise.resolve();

// Timeout per ogni singola op SQL. Valori normali: 1-50ms su native, fino a
// qualche centinaio ms su web. 8s è abbondantemente fuori dal range normale.
// Se scatta, è quasi certamente uno zombie state del plugin nativo (Android
// che ha messo SQLCipher in pausa profonda durante il background) → buttiamo
// la connection e la successiva apertura ripartirà fresca.
const SQL_OP_TIMEOUT_MS = 8000;

class SqlOpTimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`SQL op '${label}' timed out after ${ms}ms — local DB connection is stuck`);
    this.name = 'SqlOpTimeoutError';
  }
}

/** Invalidate immediately, but never reopen over a native close still in flight. */
function resetConnectionAfterStuck(reason: string): void {
  if (resettingPromise) return;
  try { console.warn('[localdb] reset_after_stuck', reason); } catch { /* noop */ }
  connection = null;
  try { sessionStorage.removeItem(ENC_SECRET_KEY); } catch { /* noop */ }
  resettingPromise = sqlite.closeConnection(DB_NAME, false)
    .catch(() => undefined)
    .finally(() => { resettingPromise = null; });
}

async function waitForConnectionReset(): Promise<void> {
  if (!resettingPromise) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      resettingPromise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('local_db_recovery_pending')), 1500);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function withTimeout<T>(label: string, op: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      // Reset asincrono — il chiamante riceve subito il timeout.
      void resetConnectionAfterStuck(`timeout:${label}`);
      reject(new SqlOpTimeoutError(label, SQL_OP_TIMEOUT_MS));
    }, SQL_OP_TIMEOUT_MS);
    op().then(
      (v) => { if (settled) return; settled = true; clearTimeout(timer); resolve(v); },
      (e) => { if (settled) return; settled = true; clearTimeout(timer); reject(e); },
    );
  });
}

function enqueueOp<T>(label: string, op: () => Promise<T>): Promise<T> {
  const trace = beginNativePerfOperation(label);
  const wrapped = (): Promise<T> => {
    if (!trace) return withTimeout(label, op);
    startNativePerfOperation(trace);
    return withTimeout(label, op).then(
      (result) => { finishNativePerfSpan(trace, 'ok'); return result; },
      (error: unknown) => {
        finishNativePerfSpan(trace, error instanceof SqlOpTimeoutError ? 'timeout' : 'error');
        throw error;
      },
    );
  };
  const p = opQueue.then(wrapped, wrapped);
  // La nuova queue ignora gli errori così un fail non rompe la catena.
  opQueue = p.then(
    () => undefined,
    () => undefined,
  );
  return p;
}

const SERIALIZED_SQL_METHODS = new Set([
  'run',
  'query',
  'execute',
  'executeSet',
  'executeTransaction',
]);

function wrapConnection(db: SQLiteDBConnection): SQLiteDBConnection {
  const wrapped = new Proxy(db, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function') return value;
      if (typeof prop === 'string' && SERIALIZED_SQL_METHODS.has(prop)) {
        const fn = value as (...args: unknown[]) => Promise<unknown>;
        return (...args: unknown[]) => enqueueOp(prop, () => {
          // Never replay a queued write on a replaced handle: its outbox owns retries.
          if (connection !== wrapped) return Promise.reject(new Error('local_db_connection_changed'));
          return fn.apply(target, args);
        });
      }
      return (value as (...args: unknown[]) => unknown).bind(target);
    },
  }) as SQLiteDBConnection;
  return wrapped;
}
// setEncryptionSecret è accettata una sola volta per lifetime del processo
// dal plugin jeep-sqlite/SQLCipher. sessionStorage sopravvive ai hot-reload
// ma non al riavvio dell'app, allineandosi al ciclo di vita del plugin nativo.
const ENC_SECRET_KEY = 'niveo.enc_secret_set';

/** Hex encode a buffer. */
function bufToHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * Derive a 256-bit SQLCipher key using PBKDF2-SHA256.
 * Secret is `${authToken}|${deviceId}` — both are required so stolen
 * device alone cannot decrypt the DB after logout/token expiry.
 */
export async function deriveDbKey(authToken: string | null, deviceId: string): Promise<string> {
  const secret = `${authToken ?? 'anonymous'}|${deviceId}`;
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(secret), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: enc.encode(PBKDF2_SALT),
      iterations: PBKDF2_ITER,
      hash: 'SHA-256',
    },
    keyMaterial,
    256,
  );
  return bufToHex(bits);
}

export async function getDeviceId(): Promise<string> {
  const existing = await Preferences.get({ key: DEVICE_ID_KEY });
  if (existing.value) return existing.value;
  const rnd = crypto.getRandomValues(new Uint8Array(16));
  const id = bufToHex(rnd.buffer);
  await Preferences.set({ key: DEVICE_ID_KEY, value: id });
  return id;
}

const SCHEMA_STATEMENTS = [
  `CREATE TABLE IF NOT EXISTS meta (
     key TEXT PRIMARY KEY,
     value TEXT NOT NULL
   );`,
  `CREATE TABLE IF NOT EXISTS pending_ops (
     id INTEGER PRIMARY KEY AUTOINCREMENT,
     kind TEXT NOT NULL CHECK (kind IN ('event','master')),
     payload TEXT NOT NULL,
     created_at TEXT NOT NULL DEFAULT (datetime('now')),
     attempts INTEGER NOT NULL DEFAULT 0,
     last_error TEXT
   );`,
  `CREATE INDEX IF NOT EXISTS idx_pending_ops_created ON pending_ops(created_at);`,
  `CREATE TABLE IF NOT EXISTS pending_op_reconciliations_v1 (
     id INTEGER PRIMARY KEY AUTOINCREMENT,
     pending_op_id INTEGER NOT NULL,
     kind TEXT NOT NULL CHECK (kind IN ('event','master')),
     payload_json TEXT NOT NULL,
     reason TEXT NOT NULL,
     attempts INTEGER NOT NULL,
     status TEXT NOT NULL DEFAULT 'reconciliation_required'
       CHECK (status IN ('reconciliation_required','resolved')),
     created_at TEXT NOT NULL,
     updated_at TEXT NOT NULL,
     resolved_at TEXT,
     UNIQUE(pending_op_id, kind)
   );`,
  `CREATE INDEX IF NOT EXISTS idx_pending_op_reconciliations_status
     ON pending_op_reconciliations_v1(status, created_at);`,
  `CREATE TABLE IF NOT EXISTS events_local (
     id TEXT PRIMARY KEY,
     server_id TEXT,
     tenant_id TEXT NOT NULL,
     kind TEXT NOT NULL,
     business_id TEXT,
     client_ts TEXT NOT NULL,
     server_ts TEXT,
     rev INTEGER,
     payload TEXT NOT NULL,
     self_hash TEXT,
     prev_hash TEXT
   );`,
  `CREATE INDEX IF NOT EXISTS idx_events_local_tenant_kind ON events_local(tenant_id, kind);`,
  `CREATE INDEX IF NOT EXISTS idx_events_local_rev ON events_local(rev);`,
  `CREATE TABLE IF NOT EXISTS pending_stock_commands (
     command_id TEXT PRIMARY KEY,
     tenant_id TEXT NOT NULL,
     digest TEXT NOT NULL,
     command_json TEXT NOT NULL,
     master_effects_json TEXT NOT NULL DEFAULT '[]',
     status TEXT NOT NULL CHECK (status IN ('pending','sending','conflict')),
     attempts INTEGER NOT NULL DEFAULT 0,
     optimistic_event_ids TEXT NOT NULL,
     last_error TEXT,
     conflict_json TEXT,
     queued_at TEXT NOT NULL,
     updated_at TEXT NOT NULL
   );`,
  `CREATE INDEX IF NOT EXISTS idx_pending_stock_commands_status
     ON pending_stock_commands(status, queued_at);`,
  `CREATE TABLE IF NOT EXISTS pending_stock_command_events (
     command_id TEXT NOT NULL,
     event_id TEXT NOT NULL,
     event_json TEXT NOT NULL,
     PRIMARY KEY (command_id, event_id)
   );`,
  `CREATE INDEX IF NOT EXISTS idx_pending_stock_command_events_command
     ON pending_stock_command_events(command_id);`,
  `CREATE TABLE IF NOT EXISTS pending_stock_command_master_snapshots (
     command_id TEXT NOT NULL,
     table_name TEXT NOT NULL,
     id TEXT NOT NULL,
     existed INTEGER NOT NULL CHECK (existed IN (0,1)),
     tenant_id TEXT,
     version INTEGER,
     row_json TEXT,
     archived INTEGER,
     updated_at TEXT,
     PRIMARY KEY (command_id, table_name, id)
   );`,
  `CREATE INDEX IF NOT EXISTS idx_stock_command_master_snapshots_command
     ON pending_stock_command_master_snapshots(command_id);`,
  `CREATE TABLE IF NOT EXISTS pending_label_print_outcomes_v1 (
     intent_id TEXT PRIMARY KEY,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     production_id TEXT NOT NULL,
     command_id TEXT,
     outcome TEXT NOT NULL CHECK (outcome IN ('printed','failed','voided')),
     occurred_at TEXT NOT NULL,
     error TEXT,
     operator_assertion_json TEXT,
     intent_snapshot_json TEXT NOT NULL,
     status TEXT NOT NULL CHECK (status IN ('pending','sending')),
     attempts INTEGER NOT NULL DEFAULT 0,
     last_error TEXT,
     queued_at TEXT NOT NULL,
     updated_at TEXT NOT NULL
   );`,
  `CREATE INDEX IF NOT EXISTS idx_label_print_outcomes_status
     ON pending_label_print_outcomes_v1(status, queued_at);`,
  `CREATE TABLE IF NOT EXISTS production_label_intent_server_receipts_v1 (
     intent_id TEXT PRIMARY KEY,
     command_id TEXT NOT NULL,
     received_at TEXT NOT NULL
   );`,
  `CREATE INDEX IF NOT EXISTS idx_label_intent_receipts_command
     ON production_label_intent_server_receipts_v1(command_id);`,
  `CREATE TABLE IF NOT EXISTS production_label_reconciliations_v1 (
     intent_id TEXT PRIMARY KEY,
     command_id TEXT NOT NULL,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     production_id TEXT NOT NULL,
     outcome TEXT NOT NULL CHECK (outcome IN ('printed','voided')),
     occurred_at TEXT NOT NULL,
     reason TEXT NOT NULL,
     intent_snapshot_json TEXT NOT NULL,
     status TEXT NOT NULL
       CHECK (status IN ('reconciliation_required','resolved')),
     resolution TEXT,
     created_at TEXT NOT NULL,
     updated_at TEXT NOT NULL,
     resolved_at TEXT
   );`,
  `CREATE INDEX IF NOT EXISTS idx_label_reconciliations_status
     ON production_label_reconciliations_v1(status, created_at);`,
  `CREATE TABLE IF NOT EXISTS legacy_stock_reconciliations_v1 (
     client_uuid TEXT PRIMARY KEY,
     pending_op_id INTEGER NOT NULL,
     tenant_id TEXT NOT NULL,
     business_id TEXT,
     event_kind TEXT NOT NULL,
     event_snapshot_json TEXT NOT NULL,
     reason TEXT NOT NULL,
     status TEXT NOT NULL
       CHECK (status IN ('reconciliation_required','resolved')),
     resolution TEXT,
     created_at TEXT NOT NULL,
     updated_at TEXT NOT NULL,
     resolved_at TEXT
   );`,
  `CREATE INDEX IF NOT EXISTS idx_legacy_stock_reconciliations_scope
     ON legacy_stock_reconciliations_v1(
       tenant_id, business_id, status, created_at
     );`,
  `CREATE TABLE IF NOT EXISTS stock_command_finalizations (
     command_id TEXT PRIMARY KEY,
     digest TEXT NOT NULL,
     outcome TEXT NOT NULL
       CHECK (outcome IN ('acknowledged','permanently_rejected')),
     reason TEXT,
     finalized_at TEXT NOT NULL
   );`,
  `CREATE TABLE IF NOT EXISTS stock_command_reconciliations_v1 (
     command_id TEXT PRIMARY KEY,
     digest TEXT NOT NULL,
     tenant_id TEXT NOT NULL,
     command_json TEXT NOT NULL,
     events_json TEXT NOT NULL,
     master_effects_json TEXT NOT NULL,
     reason TEXT NOT NULL,
     status TEXT NOT NULL
       CHECK (status IN ('reconciliation_required','resolved')),
     resolution TEXT,
     created_at TEXT NOT NULL,
     updated_at TEXT NOT NULL,
     resolved_at TEXT
   );`,
  `CREATE INDEX IF NOT EXISTS idx_stock_command_reconciliations_scope
     ON stock_command_reconciliations_v1(
       tenant_id, status, created_at
     );`,
  `CREATE TABLE IF NOT EXISTS stock_command_legacy_acknowledgements_v1 (
     command_id TEXT PRIMARY KEY,
     reason TEXT,
     resolution TEXT NOT NULL,
     resolved_at TEXT NOT NULL
   );`,
  `CREATE TABLE IF NOT EXISTS pending_atomic_rpcs_v1 (
     request_id TEXT PRIMARY KEY,
     kind TEXT NOT NULL
       CHECK (kind IN (
         'production_plan_replace_v1',
         'production_day_transition_v1',
         'stock_reconciliation_v1',
         'consultant_historical_lot_recount_v1'
       )),
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     scope_key TEXT NOT NULL,
     digest TEXT NOT NULL,
     request_json TEXT NOT NULL,
     prerequisite_request_id TEXT,
     status TEXT NOT NULL
       CHECK (status IN ('pending','sending','conflict')),
     attempts INTEGER NOT NULL DEFAULT 0,
     last_error TEXT,
     conflict_json TEXT,
     queued_at TEXT NOT NULL,
     updated_at TEXT NOT NULL
   );`,
  `CREATE UNIQUE INDEX IF NOT EXISTS idx_pending_atomic_rpcs_scope_v1
     ON pending_atomic_rpcs_v1(kind, tenant_id, scope_key);`,
  `CREATE INDEX IF NOT EXISTS idx_pending_atomic_rpcs_drain_v1
     ON pending_atomic_rpcs_v1(tenant_id, status, queued_at, request_id);`,
  `CREATE TABLE IF NOT EXISTS pending_atomic_rpc_master_snapshots_v1 (
     request_id TEXT NOT NULL,
     table_name TEXT NOT NULL,
     id TEXT NOT NULL,
     existed INTEGER NOT NULL CHECK (existed IN (0,1)),
     tenant_id TEXT,
     version INTEGER,
     row_json TEXT,
     archived INTEGER,
     updated_at TEXT,
     effect_json TEXT NOT NULL,
     PRIMARY KEY (request_id, table_name, id)
   );`,
  `CREATE INDEX IF NOT EXISTS idx_atomic_rpc_snapshots_target_v1
     ON pending_atomic_rpc_master_snapshots_v1(table_name, id);`,
  `CREATE TABLE IF NOT EXISTS atomic_rpc_dependents_v1 (
     request_id TEXT NOT NULL,
     dependent_kind TEXT NOT NULL
       CHECK (dependent_kind IN ('stock_command')),
     dependent_id TEXT NOT NULL,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     status TEXT NOT NULL
       CHECK (status IN ('pending','quarantined')),
     reason TEXT,
     queued_at TEXT NOT NULL,
     updated_at TEXT NOT NULL,
     PRIMARY KEY (request_id, dependent_kind, dependent_id)
   );`,
  `CREATE INDEX IF NOT EXISTS idx_atomic_rpc_dependents_owner_v1
     ON atomic_rpc_dependents_v1(request_id, status, queued_at);`,
  `CREATE INDEX IF NOT EXISTS idx_atomic_rpc_dependents_child_v1
     ON atomic_rpc_dependents_v1(dependent_kind, dependent_id);`,
  `CREATE TABLE IF NOT EXISTS atomic_rpc_finalizations_v1 (
     request_id TEXT PRIMARY KEY,
     kind TEXT NOT NULL,
     digest TEXT NOT NULL,
     outcome TEXT NOT NULL
       CHECK (outcome IN ('acknowledged','resolved_conflict')),
     reason TEXT,
     finalized_at TEXT NOT NULL
   );`,
  `CREATE TABLE IF NOT EXISTS masters_local (
     table_name TEXT NOT NULL,
     id TEXT NOT NULL,
     tenant_id TEXT,
     version INTEGER NOT NULL DEFAULT 0,
     row TEXT NOT NULL,
     archived INTEGER NOT NULL DEFAULT 0,
     updated_at TEXT NOT NULL DEFAULT (datetime('now')),
     PRIMARY KEY (table_name, id)
   );`,
  `CREATE INDEX IF NOT EXISTS idx_masters_local_tenant ON masters_local(tenant_id);`,
  `CREATE TABLE IF NOT EXISTS debug_events (
     id INTEGER PRIMARY KEY AUTOINCREMENT,
     ts TEXT NOT NULL DEFAULT (datetime('now')),
     area TEXT NOT NULL,
     level TEXT NOT NULL DEFAULT 'info',
     message TEXT NOT NULL,
     payload TEXT
   );`,
  `CREATE INDEX IF NOT EXISTS idx_debug_events_ts ON debug_events(ts);`,
  // T-170 Fase 1d: draft locale dell'import documenti. Persistito PRIMA del
  // primo upload (P4) così cattura+pagine sopravvivono a kill/background; la
  // ripresa ri-carica solo le pagine non ancora uploadate e poi enqueue.
  `CREATE TABLE IF NOT EXISTS document_drafts (
     draft_id TEXT PRIMARY KEY,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     doc_kind TEXT,
     state TEXT NOT NULL DEFAULT 'capturing',
     pages TEXT NOT NULL DEFAULT '[]',
     server_job_id TEXT,
     created_at TEXT NOT NULL DEFAULT (datetime('now')),
     updated_at TEXT NOT NULL DEFAULT (datetime('now'))
   );`,
  `CREATE INDEX IF NOT EXISTS idx_document_drafts_state ON document_drafts(state);`,
  // T-638: vassoio foto/file ancora NON inviato. I byte restano nel database
  // locale (SQLCipher sull'app nativa) e sono separati per account, tenant e
  // ristorante. Sul web il DB viene comunque eliminato al logout.
  `CREATE TABLE IF NOT EXISTS document_capture_trays_v1 (
     account_id TEXT NOT NULL,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     mode TEXT NOT NULL CHECK (mode IN ('separate','single')),
     items TEXT NOT NULL DEFAULT '[]',
     single_draft_id TEXT,
     updated_at TEXT NOT NULL DEFAULT (datetime('now')),
     PRIMARY KEY (account_id, tenant_id, business_id)
   );`,
  `CREATE TABLE IF NOT EXISTS work_draft_cache_v1 (
     account_id TEXT NOT NULL,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     resource_kind TEXT NOT NULL,
     resource_key TEXT NOT NULL,
     payload TEXT NOT NULL DEFAULT '{}',
     updated_at TEXT NOT NULL,
     PRIMARY KEY (account_id, tenant_id, business_id, resource_kind, resource_key)
   );`,
  // T-562: le foto lotto scattate durante una produzione devono sopravvivere
  // a offline, background e riavvio. Il base64 resta soltanto nel DB locale
  // cifrato; sul server viaggia nel bucket privato con path deterministico.
  `CREATE TABLE IF NOT EXISTS pending_production_lot_photos_v1 (
     id TEXT PRIMARY KEY,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     production_id TEXT NOT NULL,
     input_id TEXT NOT NULL,
     capture_id TEXT NOT NULL,
     command_id TEXT,
     target_path TEXT NOT NULL UNIQUE,
     mime TEXT NOT NULL,
     base64 TEXT NOT NULL,
     sha256 TEXT NOT NULL,
     size_bytes INTEGER NOT NULL CHECK (size_bytes > 0),
     status TEXT NOT NULL DEFAULT 'staged'
       CHECK (status IN ('staged','pending','uploading')),
     remote_accepted INTEGER NOT NULL DEFAULT 0
       CHECK (remote_accepted IN (0,1)),
     review_required INTEGER NOT NULL DEFAULT 0
       CHECK (review_required IN (0,1)),
     attempts INTEGER NOT NULL DEFAULT 0,
     last_error TEXT,
     next_attempt_at TEXT NOT NULL,
     queued_at TEXT NOT NULL,
     updated_at TEXT NOT NULL
   );`,
  `CREATE INDEX IF NOT EXISTS idx_production_lot_photos_drain_v1
     ON pending_production_lot_photos_v1(tenant_id, status, queued_at);`,
  `CREATE TABLE IF NOT EXISTS pending_operational_evidence_v1 (
     event_client_uuid TEXT PRIMARY KEY,
     tenant_id TEXT NOT NULL,
     business_id TEXT NOT NULL,
     evidence_kind TEXT NOT NULL CHECK (evidence_kind IN ('cleaning','temperature','register')),
     target_id TEXT NOT NULL,
     target_path TEXT NOT NULL UNIQUE,
     mime TEXT NOT NULL,
     base64 TEXT NOT NULL,
     sha256 TEXT NOT NULL,
     size_bytes INTEGER NOT NULL CHECK (size_bytes > 0),
     attempts INTEGER NOT NULL DEFAULT 0,
     last_error TEXT,
     next_attempt_at TEXT NOT NULL,
     queued_at TEXT NOT NULL,
     updated_at TEXT NOT NULL
   );`,
  `CREATE INDEX IF NOT EXISTS idx_operational_evidence_drain_v1
     ON pending_operational_evidence_v1(tenant_id, next_attempt_at, queued_at);`,
];

const DEBUG_RING_LIMIT = 1000;

/**
 * Open the encrypted local DB. Key is re-derived from the current auth token
 * and device id. If the caller later gets a new auth token the connection
 * should be closed and re-opened (handled by closeLocalDb + openLocalDb).
 */
export async function openLocalDb(authToken: string | null): Promise<SQLiteDBConnection> {
  if (openingPromise) return openingPromise;
  // Health checks and recovery share the same flight as initial opening.
  // Returning the mutable global after an await could otherwise return null.
  openingPromise = (async () => {
    await waitForConnectionReset();
    const cached = connection;
    if (cached) {
      try {
        await cached.query('SELECT 1');
        if (connection === cached) return cached;
      } catch (err) {
        logDbg('reused_dead_reopen', { error: toUserMessage(err) });
        if (connection === cached) connection = null;
        try { sessionStorage.removeItem(ENC_SECRET_KEY); } catch { /* noop */ }
      }
      await waitForConnectionReset();
    }
    const t0 = performance.now();
    const deviceId = await getDeviceId();
    // sql.js (jeep-sqlite) has no SQLCipher — encryption is native-only.
    const isWeb = Capacitor.getPlatform() === 'web';

    // A WebView reload resets the JS registry, while native SQLite handles survive.
    // Reconcile them before createConnection; this closes handles, never DB files.
    if (!isWeb) await sqlite.checkConnectionsConsistency();
    let isConn = (await sqlite.isConnection(DB_NAME, false)).result;
    logDbg('opening', { isWeb, isConn });

    // Detect "reload OTA scenario": JS-side connection è null (modulo appena
    // caricato) ma native dice isConn=true → la connessione precedente è
    // stantia (transazioni pendenti possibili). Chiudiamo lato nativo per
    // ripartire pulito invece di recuperarla. Senza questo, su Android la
    // prima query post-reload può rimanere bloccata indefinitamente e il
    // sync va in coma finché l'utente non killa l'app.
    if (!isWeb && isConn) {
      try {
        await sqlite.closeConnection(DB_NAME, false);
        logDbg('stale_native_conn_closed');
      } catch (err) {
        logDbg('stale_native_conn_close_failed', { error: toUserMessage(err) });
      }
      isConn = false;
    }

    if (!isWeb && !sessionStorage.getItem(ENC_SECRET_KEY)) {
      const key = await deriveDbKey(authToken, deviceId);
      try {
        await sqlite.setEncryptionSecret(key);
        logDbg('encryption_set');
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!/already.*set/i.test(msg)) {
          logDbg('encryption_set_failed', { error: msg });
          throw err;
        }
        logDbg('encryption_already_set_ignored');
      }
      sessionStorage.setItem(ENC_SECRET_KEY, '1');
    }
    // Dopo aver chiuso la connessione stantia sopra, qui creiamo SEMPRE una
    // nuova connessione. Manteniamo la fallback "already exists → retrieve"
    // per copertura di edge case (es. closeConnection silently fallita).
    const createOrRetrieve = async (): Promise<SQLiteDBConnection> => {
      if (isConn) return sqlite.retrieveConnection(DB_NAME, false);
      try {
        return await sqlite.createConnection(
          DB_NAME,
          !isWeb,
          isWeb ? 'no-encryption' : 'secret',
          DB_VERSION,
          false,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/already exists/i.test(msg)) {
          logDbg('createConnection_orphaned_recovered');
          return sqlite.retrieveConnection(DB_NAME, false);
        }
        throw err;
      }
    };
    const db = await createOrRetrieve();
    try {
      await db.open();
    } catch (err) {
      logDbg('db_open_failed', { error: toUserMessage(err) });
      throw err;
    }

    for (const stmt of SCHEMA_STATEMENTS) {
      await db.execute(stmt);
    }
    // T731: preserve queued photos atomically while extending the kind constraint.
    const evidenceSchema = await db.query("SELECT sql FROM sqlite_master WHERE type='table' AND name='pending_operational_evidence_v1'");
    const evidenceSql = String(evidenceSchema.values?.[0]?.sql ?? '');
    if (evidenceSql && !evidenceSql.includes("'register'")) {
      const create = SCHEMA_STATEMENTS.find(stmt => stmt.includes('CREATE TABLE IF NOT EXISTS pending_operational_evidence_v1'))!;
      await db.execute([
        create.replace('pending_operational_evidence_v1', 'pending_operational_evidence_t731'),
        'INSERT INTO pending_operational_evidence_t731 SELECT * FROM pending_operational_evidence_v1;',
        'DROP TABLE pending_operational_evidence_v1;',
        'ALTER TABLE pending_operational_evidence_t731 RENAME TO pending_operational_evidence_v1;',
        'CREATE INDEX idx_operational_evidence_drain_v1 ON pending_operational_evidence_v1(tenant_id,next_attempt_at,queued_at);',
      ].join('\n'), true);
    }
    // Migrazioni additive best-effort per i DB già esistenti (CREATE TABLE IF
    // NOT EXISTS non aggiorna lo schema): l'ALTER fallisce con "duplicate
    // column" quando la colonna c'è già → ignorato. T-221: business_id sugli
    // eventi per le giacenze per-sede (il server ce l'ha da sempre; i rows
    // locali pre-esistenti restano NULL = visibili in ogni sede).
    for (const alter of [
      `ALTER TABLE events_local ADD COLUMN business_id TEXT`,
      `ALTER TABLE events_local ADD COLUMN server_id TEXT`,
      `ALTER TABLE pending_stock_commands
         ADD COLUMN master_effects_json TEXT NOT NULL DEFAULT '[]'`,
      `ALTER TABLE pending_label_print_outcomes_v1
         ADD COLUMN command_id TEXT`,
      `ALTER TABLE pending_label_print_outcomes_v1
         ADD COLUMN operator_assertion_json TEXT`,
      `CREATE INDEX IF NOT EXISTS idx_label_print_outcomes_command
         ON pending_label_print_outcomes_v1(command_id)`,
      `ALTER TABLE pending_atomic_rpc_master_snapshots_v1
         ADD COLUMN effect_json TEXT NOT NULL DEFAULT '{}'`,
      `ALTER TABLE pending_atomic_rpcs_v1
         ADD COLUMN prerequisite_request_id TEXT`,
      `ALTER TABLE atomic_rpc_finalizations_v1
         ADD COLUMN outcome TEXT NOT NULL DEFAULT 'acknowledged'`,
      `ALTER TABLE atomic_rpc_finalizations_v1
         ADD COLUMN reason TEXT`,
      `ALTER TABLE pending_production_lot_photos_v1
         ADD COLUMN command_id TEXT`,
      `ALTER TABLE pending_production_lot_photos_v1
         ADD COLUMN remote_accepted INTEGER NOT NULL DEFAULT 0
         CHECK (remote_accepted IN (0,1))`,
      `ALTER TABLE pending_production_lot_photos_v1
         ADD COLUMN next_attempt_at TEXT NOT NULL
         DEFAULT '1970-01-01T00:00:00.000Z'`,
      `ALTER TABLE pending_production_lot_photos_v1
         ADD COLUMN sha256 TEXT`,
      `ALTER TABLE pending_production_lot_photos_v1
         ADD COLUMN review_required INTEGER NOT NULL DEFAULT 0
         CHECK (review_required IN (0,1))`,
      `CREATE INDEX IF NOT EXISTS idx_production_lot_photos_ready_v2
         ON pending_production_lot_photos_v1(
           tenant_id, status, remote_accepted, review_required,
           next_attempt_at, queued_at
         )`,
    ]) {
      try {
        await db.execute(alter);
      } catch { /* colonna già presente */ }
    }
    // T-492: SQLite cannot ALTER a CHECK constraint. Rebuild the atomic RPC
    // table once so existing installs can durably queue stock reconciliation
    // requests without weakening the allow-list for every other kind.
    const atomicKindMigration = await db.query(
      `SELECT value FROM meta
        WHERE key = 'schema.pending_atomic_rpcs.stock_reconciliation_v1'`,
    );
    if ((atomicKindMigration.values ?? []).length === 0) {
      await db.executeTransaction([
        {
          statement:
            `CREATE TABLE pending_atomic_rpcs_v1_t492 (
               request_id TEXT PRIMARY KEY,
               kind TEXT NOT NULL CHECK (kind IN (
                 'production_plan_replace_v1',
                 'production_day_transition_v1',
                 'stock_reconciliation_v1'
               )),
               tenant_id TEXT NOT NULL,
               business_id TEXT NOT NULL,
               scope_key TEXT NOT NULL,
               digest TEXT NOT NULL,
               request_json TEXT NOT NULL,
               prerequisite_request_id TEXT,
               status TEXT NOT NULL
                 CHECK (status IN ('pending','sending','conflict')),
               attempts INTEGER NOT NULL DEFAULT 0,
               last_error TEXT,
               conflict_json TEXT,
               queued_at TEXT NOT NULL,
               updated_at TEXT NOT NULL
             )`,
        },
        {
          statement:
            `INSERT INTO pending_atomic_rpcs_v1_t492(
               request_id,kind,tenant_id,business_id,scope_key,digest,
               request_json,prerequisite_request_id,status,attempts,last_error,
               conflict_json,queued_at,updated_at
             )
             SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
                    request_json,prerequisite_request_id,status,attempts,
                    last_error,conflict_json,queued_at,updated_at
               FROM pending_atomic_rpcs_v1`,
        },
        { statement: `DROP TABLE pending_atomic_rpcs_v1` },
        {
          statement:
            `ALTER TABLE pending_atomic_rpcs_v1_t492
               RENAME TO pending_atomic_rpcs_v1`,
        },
        {
          statement:
            `CREATE UNIQUE INDEX idx_pending_atomic_rpcs_scope_v1
               ON pending_atomic_rpcs_v1(kind, tenant_id, scope_key)`,
        },
        {
          statement:
            `CREATE INDEX idx_pending_atomic_rpcs_drain_v1
               ON pending_atomic_rpcs_v1(
                 tenant_id, status, queued_at, request_id
               )`,
        },
        {
          statement:
            `INSERT INTO meta(key,value) VALUES (
               'schema.pending_atomic_rpcs.stock_reconciliation_v1',
               '1'
             )`,
        },
      ], true);
    }
    // T-539: the consultant-only historical lot recount is an atomic RPC as
    // well.  Keep the explicit SQLite allow-list: a local update must never
    // silently broaden durable commands accepted by an older client.
    const historicalLotRecountMigration = await db.query(
      `SELECT value FROM meta
        WHERE key = 'schema.pending_atomic_rpcs.consultant_historical_lot_recount_v1'`,
    );
    if ((historicalLotRecountMigration.values ?? []).length === 0) {
      await db.executeTransaction([
        {
          statement:
            `CREATE TABLE pending_atomic_rpcs_v1_t539 (
               request_id TEXT PRIMARY KEY,
               kind TEXT NOT NULL CHECK (kind IN (
                 'production_plan_replace_v1',
                 'production_day_transition_v1',
                 'stock_reconciliation_v1',
                 'consultant_historical_lot_recount_v1'
               )),
               tenant_id TEXT NOT NULL,
               business_id TEXT NOT NULL,
               scope_key TEXT NOT NULL,
               digest TEXT NOT NULL,
               request_json TEXT NOT NULL,
               prerequisite_request_id TEXT,
               status TEXT NOT NULL
                 CHECK (status IN ('pending','sending','conflict')),
               attempts INTEGER NOT NULL DEFAULT 0,
               last_error TEXT,
               conflict_json TEXT,
               queued_at TEXT NOT NULL,
               updated_at TEXT NOT NULL
             )`,
        },
        {
          statement:
            `INSERT INTO pending_atomic_rpcs_v1_t539(
               request_id,kind,tenant_id,business_id,scope_key,digest,
               request_json,prerequisite_request_id,status,attempts,last_error,
               conflict_json,queued_at,updated_at
             )
             SELECT request_id,kind,tenant_id,business_id,scope_key,digest,
                    request_json,prerequisite_request_id,status,attempts,
                    last_error,conflict_json,queued_at,updated_at
               FROM pending_atomic_rpcs_v1`,
        },
        { statement: `DROP TABLE pending_atomic_rpcs_v1` },
        {
          statement:
            `ALTER TABLE pending_atomic_rpcs_v1_t539
               RENAME TO pending_atomic_rpcs_v1`,
        },
        {
          statement:
            `CREATE UNIQUE INDEX idx_pending_atomic_rpcs_scope_v1
               ON pending_atomic_rpcs_v1(kind, tenant_id, scope_key)`,
        },
        {
          statement:
            `CREATE INDEX idx_pending_atomic_rpcs_drain_v1
               ON pending_atomic_rpcs_v1(
                 tenant_id, status, queued_at, request_id
               )`,
        },
        {
          statement:
            `INSERT INTO meta(key,value) VALUES (
               'schema.pending_atomic_rpcs.consultant_historical_lot_recount_v1',
               '1'
             )`,
        },
      ], true);
    }
    await db.run(
      `INSERT OR IGNORE INTO meta(key,value) VALUES ('device_id', ?), ('last_rev', '0')`,
      [deviceId],
    );

    // Da qui in poi tutte le op SQL passano per la queue serializzata.
    connection = wrapConnection(db);
    logDbg('opened', { ms: Math.round(performance.now() - t0) });
    return connection;
  })();

  try {
    return await openingPromise;
  } catch (err) {
    logDbg('open_failed', { error: toUserMessage(err) });
    throw err;
  } finally {
    openingPromise = null;
  }
}

/** Logger interno: scrive in debug_events (se DB aperta) E in localStorage
 * sempre (sopravvive a DB chiusa / errori di apertura). Questo è critico:
 * alcune chiamate avvengono prima che la DB sia pronta o quando la DB ha
 * fallito ad aprirsi del tutto.
 */
function logDbg(message: string, payload?: Record<string, unknown>): void {
  console.info('[localdb]', message, payload ?? {});
  // Mirror sempre in localStorage (200-event ring buffer).
  try {
    const KEY = 'niveo.debug_log_v1';
    const raw = localStorage.getItem(KEY);
    const arr = raw ? (JSON.parse(raw) as Array<Record<string, unknown>>) : [];
    arr.push({
      ts: new Date().toISOString(),
      area: 'localdb',
      level: 'info',
      message,
      payload: payload ?? null,
    });
    if (arr.length > 200) arr.splice(0, arr.length - 200);
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch { /* noop */ }
  // Mirror anche in debug_events se DB c'è.
  if (!connection) return;
  try {
    void connection.run(
      `INSERT INTO debug_events(area, level, message, payload) VALUES(?,?,?,?)`,
      ['localdb', 'info', message, payload ? JSON.stringify(payload) : null],
    ).catch(() => undefined);
  } catch { /* noop */ }
}

/**
 * Persist the in-memory web DB to IndexedDB. No-op on native (where writes
 * are flushed by the OS-backed SQLite engine directly).
 */
export async function flushLocalDb(options: { required?: boolean } = {}): Promise<void> {
  if (Capacitor.getPlatform() !== 'web') return;
  try {
    await enqueueOp('saveToStore', () => sqlite.saveToStore(DB_NAME));
  } catch (error) {
    if (options.required) throw error;
    /* saveToStore throws if DB not open; benign. */
  }
}

export async function closeLocalDb(): Promise<void> {
  if (openingPromise) await openingPromise.catch(() => undefined);
  await waitForConnectionReset();
  const cached = connection;
  if (!cached) return;
  connection = null;
  resettingPromise = (async () => {
    await opQueue;
    try {
      await cached.close();
    } finally {
      await sqlite.closeConnection(DB_NAME, false).catch(() => undefined);
    }
  })().finally(() => { resettingPromise = null; });
  await resettingPromise;
}

export async function getMeta(key: string): Promise<string | null> {
  const db = await openLocalDb(null);
  const res = await db.query(`SELECT value FROM meta WHERE key = ?`, [key]);
  const row = res.values?.[0] as { value?: string } | undefined;
  return row?.value ?? null;
}

export async function setMeta(key: string, value: string): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `INSERT INTO meta(key,value) VALUES(?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
    [key, value],
  );
}

export interface PendingOp {
  kind: 'event' | 'master';
  payload: unknown;
}

export async function enqueuePendingOp(op: PendingOp): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `INSERT INTO pending_ops(kind, payload) VALUES (?, ?)`,
    [op.kind, JSON.stringify(op.payload)],
  );
}

export interface PendingOpRow extends PendingOp {
  id: number;
  attempts: number;
  lastError?: string | null;
  blockedByAtomicRequestId?: string;
  invalidPayloadReason?: string;
  ownerAccountId?: string;
}

export interface PendingRegisterScope {
  accountId: string;
  tenantId: string | null;
  businessId: string | null;
  operator?: {
    accountUserId: string | null;
    tenantPersonnelId: string;
    grantExpiresAt: string;
    deviceId: string;
  } | null;
}

export const REGISTER_OWNER_FIELD = '__trackeat_register_account_id';

const ATOMIC_PREREQUISITE_FIELD =
  '__trackeat_atomic_prerequisite_request_id';

function parsePendingOpRow(r: unknown): PendingOpRow {
  const row = r as {
    id: number;
    kind: 'event' | 'master';
    payload: string;
    attempts: number;
    last_error?: string | null;
  };
  let parsed: unknown;
  try {
    parsed = JSON.parse(row.payload) as unknown;
  } catch (error) {
    return {
      id: row.id,
      kind: row.kind,
      attempts: row.attempts,
      lastError: row.last_error ?? null,
      // Preserve the exact corrupt text in the reconciliation ledger instead
      // of letting one unreadable row crash every later queue item.
      payload: row.payload,
      invalidPayloadReason: `local_payload_json_invalid:${toUserMessage(error)}`,
    };
  }
  let payload = parsed;
  let blockedByAtomicRequestId: string | undefined;
  if (
    parsed
    && typeof parsed === 'object'
    && !Array.isArray(parsed)
  ) {
    const record = parsed as Record<string, unknown>;
    if (typeof record[ATOMIC_PREREQUISITE_FIELD] === 'string') {
      blockedByAtomicRequestId =
        record[ATOMIC_PREREQUISITE_FIELD] as string;
      const publicPayload = { ...record };
      delete publicPayload[ATOMIC_PREREQUISITE_FIELD];
      payload = publicPayload;
    }
  }
  const ownerAccountId = payload && typeof payload === 'object'
    ? (payload as Record<string, unknown>)[REGISTER_OWNER_FIELD] : undefined;
  if (payload && typeof payload === 'object' && REGISTER_OWNER_FIELD in payload) {
    payload = { ...(payload as Record<string, unknown>) };
    delete (payload as Record<string, unknown>)[REGISTER_OWNER_FIELD];
  }
  return {
    id: row.id,
    kind: row.kind,
    attempts: row.attempts,
    lastError: row.last_error ?? null,
    payload,
    ...(typeof ownerAccountId === 'string' ? { ownerAccountId } : {}),
    ...(blockedByAtomicRequestId
      ? { blockedByAtomicRequestId }
      : {}),
  };
}

/**
 * Operations on the same master stay causally ordered. An isolated failure on
 * one master must not, however, hold unrelated masters or append-only events.
 */
export function pendingOpIsolationKeyV1(
  row: Pick<PendingOpRow, 'id' | 'kind' | 'payload'>,
): string {
  if (
    row.kind === 'master'
    && row.payload
    && typeof row.payload === 'object'
    && !Array.isArray(row.payload)
  ) {
    const payload = row.payload as { table?: unknown; id?: unknown };
    if (typeof payload.table === 'string' && typeof payload.id === 'string') {
      return `master:${payload.table}:${payload.id}`;
    }
  }
  return `pending:${row.id}`;
}

export function pendingMasterPayloadBlockedByAtomicRequest(
  op: Record<string, unknown>,
  requestId: string,
): Record<string, unknown> {
  return {
    ...op,
    [ATOMIC_PREREQUISITE_FIELD]: requestId,
  };
}

export function pendingOpIsRunnableV1(
  row: Pick<PendingOpRow, 'blockedByAtomicRequestId'>,
  activeAtomicRequestIds: ReadonlySet<string>,
): boolean {
  return !row.blockedByAtomicRequestId
    || !activeAtomicRequestIds.has(row.blockedByAtomicRequestId);
}

export async function listPendingOps(limit = 200): Promise<PendingOpRow[]> {
  const db = await openLocalDb(null);
  const res = await db.query(
    `SELECT id, kind, payload, attempts, last_error
       FROM pending_ops ORDER BY id ASC LIMIT ?`,
    [limit],
  );
  return (res.values ?? []).map(parsePendingOpRow);
}

/**
 * FIFO rows whose atomic prerequisite has ACKed (and therefore left the
 * active atomic outbox). Blocked rows remain counted and durable but can never
 * overtake a pending reopen after an offline restart.
 */
export async function listRunnablePendingOps(
  limit = 200,
  excludedIsolationKeys: ReadonlySet<string> = new Set(),
  scope?: PendingRegisterScope,
): Promise<PendingOpRow[]> {
  const db = await openLocalDb(null);
  const rowsResult = await db.query(
    `SELECT id, kind, payload, attempts, last_error
       FROM pending_ops
      ORDER BY id ASC`,
  );
  const blockersResult = await db.query(
    `SELECT request_id
       FROM pending_atomic_rpcs_v1
      WHERE status IN ('pending','sending','conflict')`,
  );
  const evidenceResult = await db.query(
    `SELECT event_client_uuid,target_path,evidence_kind FROM pending_operational_evidence_v1`,
  );
  const activeBlockers = new Set(
    (blockersResult.values ?? []).map(
      (value) => (value as { request_id: string }).request_id,
    ),
  );
  const blockedEvidenceEvents = new Set(
    (evidenceResult.values ?? []).map(
      (value) => String((value as { event_client_uuid?: unknown }).event_client_uuid ?? ''),
    ),
  );
  const blockedRegisterPhotos = new Set((evidenceResult.values ?? [])
    .filter(value => value.evidence_kind === 'register')
    .map(value => String(value.target_path)));
  const seenIsolationKeys = new Set<string>();
  return (rowsResult.values ?? [])
    .map(parsePendingOpRow)
    .filter((row) => pendingRegisterMatchesScope(row, scope))
    .filter((row) => pendingRegisterMatchesOperator(row, scope))
    // Only the oldest row in a master scope is eligible. This preserves causal
    // order even when failed scopes are moved behind untouched independent work.
    .filter((row) => {
      const key = pendingOpIsolationKeyV1(row);
      if (seenIsolationKeys.has(key)) return false;
      seenIsolationKeys.add(key);
      return true;
    })
    .filter((row) => pendingOpIsRunnableV1(row, activeBlockers))
    .filter((row) => {
      if (row.kind === 'master') {
        const op = row.payload as MasterOp;
        return !isOfflineRegisterTable(op?.table) || !blockedRegisterPhotos.has(String(op.patch?.foto_storage_path ?? ''));
      }
      if (
        row.kind !== 'event'
        || !row.payload
        || typeof row.payload !== 'object'
        || Array.isArray(row.payload)
      ) return true;
      return !blockedEvidenceEvents.has(String(
        (row.payload as { client_uuid?: unknown }).client_uuid ?? '',
      ));
    })
    .filter((row) => !excludedIsolationKeys.has(pendingOpIsolationKeyV1(row)))
    .sort((left, right) => left.attempts - right.attempts || left.id - right.id)
    .slice(0, limit);
}

/** New register operations stay with their original account, even after logout. */
export function pendingRegisterMatchesScope(
  row: Pick<PendingOpRow, 'kind' | 'payload' | 'ownerAccountId'>,
  scope?: PendingRegisterScope,
): boolean {
  if (row.kind !== 'master' || !row.payload || typeof row.payload !== 'object') return true;
  const op = row.payload as { table?: unknown; patch?: { tenant_id?: unknown; business_id?: unknown } };
  if (!isOfflineRegisterTable(op.table)) return true;
  return !!scope && row.ownerAccountId === scope.accountId && op.patch?.tenant_id === scope.tenantId
    && (op.patch?.business_id ?? null) === scope.businessId;
}

/** Delivery may renew an assertion only for the same verified operator. Counts
 * deliberately use account scope alone so another operator's work stays visible. */
export function pendingRegisterMatchesOperator(
  row: Pick<PendingOpRow, 'kind' | 'payload'>,
  scope?: PendingRegisterScope,
): boolean {
  if (row.kind !== 'master' || !row.payload || typeof row.payload !== 'object') return true;
  const op = row.payload as MasterOp;
  if (!isOfflineRegisterTable(op.table)) return true;
  const actorField = op.table === 'non_conformities' ? 'rilevata_da_user_id'
    : op.table === 'food_transports' ? 'operatore_user_id' : 'registrato_da_user_id';
  const operator = scope?.operator;
  if (!op.operator_assertion) return !operator && op.patch[actorField] === scope?.accountId;
  const quickRegister = ['pest_control_logs', 'oil_control_logs', 'acrylamide_control_logs'].includes(op.table);
  const actor = operator?.accountUserId ?? (quickRegister ? scope?.accountId : null);
  const legacyLinkedPest = op.table === 'pest_control_logs' && op.patch.scheda_version == null
    && op.patch.operatore_personnel_id == null && !!operator?.accountUserId;
  return !!operator && actor === op.patch[actorField]
    && (!quickRegister || legacyLinkedPest || op.patch.operatore_personnel_id === operator.tenantPersonnelId)
    && operator.tenantPersonnelId === op.operator_assertion.tenant_personnel_id
    && operator.deviceId === op.device_id
    && Date.parse(operator.grantExpiresAt) > Date.now();
}

export async function deletePendingOp(id: number): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(`DELETE FROM pending_ops WHERE id = ?`, [id]);
}

/**
 * Terminal server rejections leave the runnable FIFO but are never discarded.
 * The immutable payload and the exact rejection are moved atomically to a
 * local dead-letter ledger so support/recovery code can repair and replay it.
 */
export async function quarantinePendingOp(
  pending: PendingOpRow,
  reason: string,
): Promise<void> {
  const db = await openLocalDb(null);
  const now = new Date().toISOString();
  await db.executeTransaction([
    {
      statement:
        `INSERT INTO pending_op_reconciliations_v1(
           pending_op_id, kind, payload_json, reason, attempts, status,
           created_at, updated_at
         ) VALUES (?, ?, ?, ?, ?, 'reconciliation_required', ?, ?)
         ON CONFLICT(pending_op_id, kind) DO UPDATE SET
           payload_json = excluded.payload_json,
           reason = excluded.reason,
           attempts = excluded.attempts,
           updated_at = excluded.updated_at`,
      values: [
        pending.id,
        pending.kind,
        JSON.stringify(pending.ownerAccountId
          ? { ...(pending.payload as object), [REGISTER_OWNER_FIELD]: pending.ownerAccountId }
          : pending.payload),
        reason,
        pending.attempts + 1,
        now,
        now,
      ],
    },
    {
      statement: `DELETE FROM pending_ops WHERE id = ?`,
      values: [pending.id],
    },
  ], true);
}

export async function countPendingOpReconciliations(): Promise<number> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT COUNT(*) AS count
       FROM pending_op_reconciliations_v1
      WHERE status = 'reconciliation_required'`,
  );
  return Number((result.values?.[0] as { count?: number } | undefined)?.count ?? 0);
}

export interface RecoverablePendingOpReconciliation {
  id: number;
  reason: (typeof RECOVERABLE_SYNC_CONTRACT_REASONS)[number];
  event: {
    kind: string;
    payload: Record<string, unknown>;
  };
}

function parseRecoverablePendingOpReconciliation(value: unknown): RecoverablePendingOpReconciliation | null {
  const row = value as { id?: unknown; reason?: unknown; payload_json?: unknown };
  if (!Number.isInteger(row.id) || typeof row.reason !== 'string' || typeof row.payload_json !== 'string') {
    return null;
  }
  if (!RECOVERABLE_SYNC_CONTRACT_REASONS.includes(
    row.reason as (typeof RECOVERABLE_SYNC_CONTRACT_REASONS)[number],
  )) return null;
  try {
    const parsed = JSON.parse(row.payload_json) as unknown;
    if (!isRecoverableSyncContractEvent(row.reason, parsed)) return null;
    return {
      id: row.id as number,
      reason: row.reason as (typeof RECOVERABLE_SYNC_CONTRACT_REASONS)[number],
      event: parsed,
    };
  } catch {
    return null;
  }
}

/**
 * Returns only records whose original event still matches a narrow, repaired
 * contract. Other semantic rejections are never surfaced as recoverable.
 */
export async function listRecoverablePendingOpReconciliations(): Promise<RecoverablePendingOpReconciliation[]> {
  const db = await openLocalDb(null);
  const reasonPlaceholders = RECOVERABLE_SYNC_CONTRACT_REASONS.map(() => '?').join(', ');
  const result = await db.query(
    `SELECT id, reason, payload_json
       FROM pending_op_reconciliations_v1
      WHERE kind = 'event'
        AND status = 'reconciliation_required'
        AND reason IN (${reasonPlaceholders})
      ORDER BY created_at ASC`,
    [...RECOVERABLE_SYNC_CONTRACT_REASONS],
  );
  return (result.values ?? [])
    .map(parseRecoverablePendingOpReconciliation)
    .filter((value): value is RecoverablePendingOpReconciliation => value !== null);
}

/** Prepare one recovery transaction; a confirmed equivalent is linked, never replayed. */
async function pendingContractRecoveryStatements(
  db: SQLiteDBConnection, recoveries: readonly RecoverablePendingOpReconciliation[], now: string,
): Promise<Array<{ statement: string; values: Array<string | number> }>> {
  const cleanings = recoveries.filter((row) => row.event.kind === 'cleaning_done');
  const tenants = [...new Set(cleanings.flatMap((row) => {
    const tenant = (row.event as unknown as {tenant_id?: unknown}).tenant_id;
    return typeof tenant === 'string' ? [tenant] : [];
  }))];
  const result = tenants.length > 0 ? await db.query(
    `SELECT kind, id, server_id, tenant_id, business_id, client_ts, rev, self_hash, payload
       FROM events_local WHERE kind = 'cleaning_done' AND rev > 0
        AND tenant_id IN (${tenants.map(() => '?').join(',')})
      ORDER BY rev ASC`,
    tenants,
  ) : { values: [] };
  const confirmed = (result.values ?? []).flatMap((row) => {
    try { return [{ ...row, payload: JSON.parse(row.payload as string) } as ConfirmedCleaningRecoveryEvent]; }
    catch { return []; }
  });
  return recoveries.flatMap((row) => {
    const existing = confirmedEquivalentCleaning(row.event, confirmed);
    const event = row.event as unknown as {client_uuid: string; tenant_id: string; business_id: string};
    return [
      ...(existing ? [{
        statement: `INSERT INTO meta(key,value) VALUES(?,?)
                     ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
        values: [`recovery.pending_op_equivalent:${row.id}`, JSON.stringify({
          reason: 'already_recorded', original_client_uuid: event.client_uuid,
          confirmed_client_uuid: existing.id, server_id: existing.server_id,
        })],
      }, {
        // The immutable rejected envelope stays in the ledger. Only an
        // unconfirmed local projection of that same envelope can be removed.
        statement: `DELETE FROM events_local WHERE id = ? AND tenant_id = ? AND business_id = ?
                       AND kind = 'cleaning_done' AND coalesce(rev,0) <= 0`,
        values: [event.client_uuid, event.tenant_id, event.business_id],
      }] : [{
        statement: `INSERT INTO pending_ops(kind, payload, created_at, attempts, last_error)
                    VALUES ('event', ?, ?, 0, NULL)`,
        values: [JSON.stringify(row.event), now],
      }]), {
        statement: `UPDATE pending_op_reconciliations_v1
                       SET status = 'resolved', updated_at = ?, resolved_at = ?
                     WHERE id = ? AND status = 'reconciliation_required'`,
        values: [now, now, row.id],
      },
    ];
  });
}

/**
 * Links a confirmed equivalent cleaning or requeues the original client UUID.
 * The preserved audit envelope is never changed; only a canonical server fact
 * can establish that the activity was already recorded.
 */
export async function requeueRecoverablePendingOpReconciliation(id: number): Promise<boolean> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT id, reason, payload_json
       FROM pending_op_reconciliations_v1
      WHERE id = ?
        AND kind = 'event'
        AND status = 'reconciliation_required'`,
    [id],
  );
  const reconciliation = parseRecoverablePendingOpReconciliation(result.values?.[0]);
  if (!reconciliation) return false;

  const now = new Date().toISOString();
  await db.executeTransaction(await pendingContractRecoveryStatements(db, [reconciliation], now), true);
  return true;
}

/** One replay of preserved printer operations after their size contract is fixed. */
export async function requeueLegacyPrinterRejectionsOnce(): Promise<number> {
  const recoveryKey = 'recovery.t710.legacy-printer-size.v1';
  if (await getMeta(recoveryKey)) return 0;
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT id, payload_json FROM pending_op_reconciliations_v1
      WHERE kind = 'master' AND status = 'reconciliation_required'
        AND reason = 'master_column_not_allowed:label_size_mm'
      ORDER BY created_at ASC`,
  );
  const tasks: Array<{ statement: string; values: unknown[] }> = [];
  const now = new Date().toISOString();
  for (const row of result.values ?? []) {
    let raw: unknown;
    try { raw = JSON.parse(String(row.payload_json)); } catch { continue; }
    const parsed = MasterOpSchema.safeParse(raw);
    if (!parsed.success) continue;
    const normalized = normalizeLegacyPrinterMaster(parsed.data);
    if (normalized === parsed.data) continue;
    tasks.push({
      statement: `INSERT INTO pending_ops(kind, payload, created_at, attempts, last_error)
                  VALUES ('master', ?, ?, 0, NULL)`,
      values: [JSON.stringify(normalized), now],
    }, {
      statement: `UPDATE pending_op_reconciliations_v1 SET status = 'resolved', updated_at = ?, resolved_at = ?
                  WHERE id = ? AND status = 'reconciliation_required'`,
      values: [now, now, row.id],
    });
  }
  const count = tasks.length / 2;
  if (count === 0) return 0;
  tasks.push({
    statement: `INSERT INTO meta(key,value) VALUES(?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
    values: [recoveryKey, now],
  });
  await db.executeTransaction(tasks, true);
  return count;
}

const SHARED_TABLET_HACCP_RECOVERY_KEY =
  'recovery.t717.shared-tablet-haccp.v3:';

/** Replays preserved signed HACCP entries once the T-698 trigger is live. */
export async function requeueSharedTabletHaccpRejectionsOnce(tenantId: string): Promise<number> {
  const recoveryKey = SHARED_TABLET_HACCP_RECOVERY_KEY + tenantId;
  if (await getMeta(recoveryKey)) return 0;
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT id, reason, payload_json
       FROM pending_op_reconciliations_v1
      WHERE kind = 'event'
        AND status = 'reconciliation_required'
        AND reason IN ('insert_failed:42501', 'insert_failed')
        AND json_extract(payload_json, '$.tenant_id') = ?
      ORDER BY created_at ASC`,
    [tenantId],
  );
  const recoveries = (result.values ?? [])
    .map(parseRecoverablePendingOpReconciliation)
    .filter((value): value is RecoverablePendingOpReconciliation => value !== null);
  if (recoveries.length === 0) return 0;

  const now = new Date().toISOString();
  await db.executeTransaction([
    ...await pendingContractRecoveryStatements(db, recoveries, now),
    {
      statement:
        `INSERT INTO meta(key,value) VALUES(?,?)
         ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
      values: [recoveryKey, now],
    },
  ], true);
  return recoveries.length;
}

const CLEANING_PRODUCT_REFERENCE_RECOVERY_KEY =
  'recovery.t686.cleaning-product-reference.v1';

/** Replays preserved T-686 operations once, only for local detergent foods. */
export async function requeueCleaningProductReferenceRejectionsOnce(): Promise<number> {
  if (await getMeta(CLEANING_PRODUCT_REFERENCE_RECOVERY_KEY)) return 0;
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT id, kind, payload_json
       FROM pending_op_reconciliations_v1
      WHERE status = 'reconciliation_required'
        AND (
          (kind = 'event' AND reason = 'event_reference_business_mismatch:cleaning_products')
          OR
          (kind = 'master' AND reason = 'master_reference_business_mismatch:cleaning_products')
        )
      ORDER BY created_at ASC`,
  );
  if ((result.values?.length ?? 0) === 0) return 0;

  const detergentCache = new Map<string, boolean>();
  const isLocalDetergent = async (id: string, tenantId: string): Promise<boolean> => {
    const key = `${tenantId}:${id}`;
    const cached = detergentCache.get(key);
    if (cached !== undefined) return cached;
    const foodResult = await db.query(
      `SELECT row FROM masters_local
        WHERE table_name = 'foods' AND id = ? AND tenant_id = ?`,
      [id, tenantId],
    );
    const rowJson = (foodResult.values?.[0] as { row?: unknown } | undefined)?.row;
    let valid = false;
    if (typeof rowJson === 'string') {
      try {
        const food = JSON.parse(rowJson) as { category?: unknown };
        valid = food.category === 'detersivi';
      } catch {
        valid = false;
      }
    }
    detergentCache.set(key, valid);
    return valid;
  };

  const recoveries: Array<{ id: number; kind: 'event' | 'master'; payload: unknown }> = [];
  for (const value of result.values ?? []) {
    const candidate = value as { id?: unknown; kind?: unknown; payload_json?: unknown };
    if (
      !Number.isInteger(candidate.id) ||
      (candidate.kind !== 'event' && candidate.kind !== 'master') ||
      typeof candidate.payload_json !== 'string'
    ) continue;
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(candidate.payload_json) as Record<string, unknown>;
    } catch {
      continue;
    }

    let tenantId: string | null = null;
    let productIds: string[] = [];
    if (candidate.kind === 'event') {
      const eventPayload = payload.payload as Record<string, unknown> | undefined;
      if (typeof payload.tenant_id === 'string' && typeof eventPayload?.product_id === 'string') {
        tenantId = payload.tenant_id;
        productIds = [eventPayload.product_id];
      }
    } else {
      if (payload.table !== 'cleaning_schedules' || typeof payload.id !== 'string') continue;
      const schedule = await db.query(
        `SELECT tenant_id FROM masters_local
          WHERE table_name = 'cleaning_schedules' AND id = ?`,
        [payload.id],
      );
      const localTenantId = (schedule.values?.[0] as { tenant_id?: unknown } | undefined)?.tenant_id;
      if (typeof localTenantId !== 'string') continue;
      tenantId = localTenantId;
      const patch = payload.patch as Record<string, unknown> | undefined;
      if (typeof patch?.product_id === 'string') productIds.push(patch.product_id);
      if (Array.isArray(patch?.product_ids)) {
        productIds.push(...patch.product_ids.filter((id): id is string => typeof id === 'string'));
      }
      productIds = [...new Set(productIds)];
    }
    if (!tenantId || productIds.length === 0) continue;
    const validProducts = await Promise.all(productIds.map((id) => isLocalDetergent(id, tenantId)));
    if (!validProducts.every(Boolean)) continue;
    recoveries.push({
      id: candidate.id as number,
      kind: candidate.kind,
      payload,
    });
  }
  if (recoveries.length === 0) return 0;

  const now = new Date().toISOString();
  await db.executeTransaction([
    ...recoveries.flatMap((recovery) => [{
      statement: `INSERT INTO pending_ops(kind, payload, created_at, attempts, last_error)
                  VALUES (?, ?, ?, 0, NULL)`,
      values: [recovery.kind, JSON.stringify(recovery.payload), now],
    }, {
      statement: `UPDATE pending_op_reconciliations_v1
                     SET status = 'resolved', updated_at = ?, resolved_at = ?
                   WHERE id = ? AND status = 'reconciliation_required'`,
      values: [now, now, recovery.id],
    }]),
    {
      statement:
        `INSERT INTO meta(key,value) VALUES(?,?)
         ON CONFLICT(key) DO UPDATE SET value=excluded.value`,
      values: [CLEANING_PRODUCT_REFERENCE_RECOVERY_KEY, now],
    },
  ], true);
  return recoveries.length;
}

export interface PendingMasterMatch {
  /** The pending_ops autoincrement row id (pass to deletePendingOp). */
  rowId: number;
  /** The queued MasterOp payload, parsed (includes device_id + client_ts). */
  op: Record<string, unknown>;
  blockedByAtomicRequestId?: string;
}

/**
 * Find a queued MASTER op by its (table, id) pair. `payload` is opaque JSON, so
 * we scan all master ops and match in JS (the table+id live inside the payload).
 * Scans the WHOLE outbox (no LIMIT) so it stays correct even when the op sits
 * beyond the regular push batch — the exact "FIFO hostage" case that left a
 * just-created production unsent. Returns the row id (so the caller can drop it
 * after a confirmed targeted push) plus the parsed op. null if not queued
 * (already drained by a concurrent tick).
 */
export async function findPendingMasterOp(
  table: string,
  id: string,
): Promise<PendingMasterMatch | null> {
  const db = await openLocalDb(null);
  const res = await db.query(
    `SELECT id, payload FROM pending_ops WHERE kind = 'master' ORDER BY id ASC`,
  );
  for (const r of res.values ?? []) {
    const row = r as { id: number; payload: string };
    let parsed: PendingOpRow;
    try {
      parsed = parsePendingOpRow({
        id: row.id,
        kind: 'master',
        payload: row.payload,
        attempts: 0,
      });
    } catch {
      continue; // defensive: skip a corrupt payload rather than throw
    }
    const op = parsed.payload as Record<string, unknown>;
    if (op.table === table && op.id === id) {
      return {
        rowId: row.id,
        op,
        ...(parsed.blockedByAtomicRequestId
          ? { blockedByAtomicRequestId: parsed.blockedByAtomicRequestId }
          : {}),
      };
    }
  }
  return null;
}

export type DebugLevel = 'info' | 'warn' | 'error';

export interface DebugEventRow {
  id: number;
  ts: string;
  area: string;
  level: DebugLevel;
  message: string;
  payload: unknown;
}

export async function appendDebugEvent(
  area: string,
  level: DebugLevel,
  message: string,
  payload?: unknown,
): Promise<void> {
  try {
    const db = await openLocalDb(null);
    await db.run(
      `INSERT INTO debug_events(area, level, message, payload) VALUES(?,?,?,?)`,
      [area, level, message, payload === undefined ? null : JSON.stringify(payload)],
    );
    // Trim ring buffer.
    await db.run(
      `DELETE FROM debug_events WHERE id IN (
         SELECT id FROM debug_events ORDER BY id DESC LIMIT -1 OFFSET ?
       )`,
      [DEBUG_RING_LIMIT],
    );
  } catch {
    // Logger MUST never throw.
  }
}

export async function listDebugEvents(limit = 200): Promise<DebugEventRow[]> {
  try {
    const db = await openLocalDb(null);
    const res = await db.query(
      `SELECT id, ts, area, level, message, payload FROM debug_events ORDER BY id DESC LIMIT ?`,
      [limit],
    );
    return (res.values ?? []).map((r) => {
      const row = r as { id: number; ts: string; area: string; level: DebugLevel; message: string; payload: string | null };
      let payload: unknown = null;
      if (row.payload) {
        try { payload = JSON.parse(row.payload); } catch { payload = row.payload; }
      }
      return { id: row.id, ts: row.ts, area: row.area, level: row.level, message: row.message, payload };
    });
  } catch {
    return [];
  }
}

export async function clearDebugEvents(): Promise<void> {
  try {
    const db = await openLocalDb(null);
    await db.run(`DELETE FROM debug_events`);
  } catch {
    /* noop */
  }
}

/** Recupera eventi non ancora spediti al server (id > lastSentId). */
export async function listDebugEventsAfter(lastId: number, limit = 100): Promise<DebugEventRow[]> {
  try {
    const db = await openLocalDb(null);
    const res = await db.query(
      `SELECT id, ts, area, level, message, payload
         FROM debug_events
        WHERE id > ?
        ORDER BY id ASC
        LIMIT ?`,
      [lastId, limit],
    );
    return (res.values ?? []).map((r) => {
      const row = r as { id: number; ts: string; area: string; level: DebugLevel; message: string; payload: string | null };
      let payload: unknown = null;
      if (row.payload) {
        try { payload = JSON.parse(row.payload); } catch { payload = row.payload; }
      }
      return { id: row.id, ts: row.ts, area: row.area, level: row.level, message: row.message, payload };
    });
  } catch {
    return [];
  }
}

export async function markPendingOpFailed(id: number, reason: string): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE pending_ops SET attempts = attempts + 1, last_error = ? WHERE id = ?`,
    [reason, id],
  );
}
